/*
	Cart timer bar engine.

	The cart drawer is refreshed by assigning innerHTML / swapping nodes parsed with DOMParser.
	Neither path re-executes a <script> that lives inside the drawer markup, so the timer cannot
	own per-element JavaScript state — it would be destroyed on the first cart update and never
	rebuilt. This engine therefore lives outside the drawer and holds no element state: every
	pass re-reads the DOM plus the persisted end time, so a bar that was just re-rendered is
	correct on the frame it appears.

	Markup contract (snippets/cart-banner-root.liquid):
		[data-cart-timer="<block id>"]   timer bar root
		[data-timer-duration="<ms>"]     countdown length
		[data-cart-timer-text]           countdown wrapper
		[data-cart-timer-minutes]        minutes pill
		[data-cart-timer-seconds]        seconds pill
		[data-timer-state]               written here: running | expired | empty

	Lifecycle: the countdown starts when the cart becomes non-empty, survives cart updates and
	page loads, and stays expired once it hits zero. Emptying the cart ends the session and the
	next add-to-cart starts a fresh countdown.
*/
(function () {
	"use strict";

	var STORE_PREFIX = "elxr:cart-timer:";
	var LEGACY_PREFIXES = ["cart_timer_", "cart_timer_cart_"];
	var DEFAULT_DURATION = 600000;
	var TICK_MS = 250;

	var tickHandle = null;
	var renderScheduled = false;
	var clearedForEmptyCart = false;
	var watchedDrawers = new WeakSet();
	var domObserver = null;
	var drawerObserver = null;

	function isDesignMode() {
		return !!(window.Shopify && window.Shopify.designMode);
	}

	function safeGet(key) {
		try {
			return localStorage.getItem(key);
		} catch (e) {
			return null;
		}
	}

	function safeSet(key, value) {
		try {
			localStorage.setItem(key, value);
		} catch (e) {
			/* private mode / quota — the timer still runs for this page view */
		}
	}

	function safeRemove(key) {
		try {
			localStorage.removeItem(key);
		} catch (e) {
			/* nothing to do */
		}
	}

	function isTimerKey(key) {
		if (key.indexOf(STORE_PREFIX) === 0) return true;
		for (var i = 0; i < LEGACY_PREFIXES.length; i++) {
			if (key.indexOf(LEGACY_PREFIXES[i]) === 0) return true;
		}
		return false;
	}

	function clearPersistedState() {
		var stale = [];
		try {
			for (var i = 0; i < localStorage.length; i++) {
				var key = localStorage.key(i);
				if (key && isTimerKey(key)) stale.push(key);
			}
		} catch (e) {
			return;
		}
		for (var s = 0; s < stale.length; s++) safeRemove(stale[s]);
	}

	function durationOf(bar) {
		var duration = parseInt(bar.getAttribute("data-timer-duration"), 10);
		if (!isFinite(duration) || duration <= 0) return DEFAULT_DURATION;
		return duration;
	}

	function writeState(id, state) {
		safeSet(STORE_PREFIX + id, JSON.stringify(state));
	}

	function readState(id, duration, now) {
		var raw = safeGet(STORE_PREFIX + id);
		var state = null;

		if (raw) {
			try {
				state = JSON.parse(raw);
			} catch (e) {
				state = null;
			}
		}

		// A changed duration means the merchant edited the setting, so the old end time is void.
		var usable = !!state && typeof state.end === "number" && state.duration === duration;
		// Merchants need to see a live countdown while editing, never a bar frozen as expired.
		if (usable && isDesignMode() && (state.expired || state.end <= now)) usable = false;

		if (!usable) {
			state = { end: now + duration, duration: duration, expired: false };
			writeState(id, state);
		}

		return state;
	}

	function pad(value) {
		return value < 10 ? "0" + value : String(value);
	}

	function format(remaining) {
		var total = Math.max(0, Math.ceil(remaining / 1000));
		return {
			minutes: pad(Math.floor(total / 60)),
			seconds: pad(total % 60),
		};
	}

	function paint(bar, state, remaining) {
		if (bar.getAttribute("data-timer-state") !== state) bar.setAttribute("data-timer-state", state);
		if (state !== "running") return;

		var text = bar.querySelector("[data-cart-timer-text]");
		if (!text) return;

		var next = format(remaining);
		var minutes = text.querySelector("[data-cart-timer-minutes]");
		var seconds = text.querySelector("[data-cart-timer-seconds]");

		if (minutes && seconds) {
			if (minutes.textContent !== next.minutes) minutes.textContent = next.minutes;
			if (seconds.textContent !== next.seconds) seconds.textContent = next.seconds;
			text.setAttribute("aria-label", next.minutes + ":" + next.seconds);
			return;
		}

		var value = next.minutes + ":" + next.seconds;
		var node = text.firstChild;

		// Mutate the text node in place where possible: assigning textContent replaces children,
		// which would wake the DOM observer every second for a change this engine just made.
		if (node && node.nodeType === 3 && !node.nextSibling) {
			if (node.nodeValue !== value) node.nodeValue = value;
		} else if (text.textContent !== value) {
			text.textContent = value;
		}
	}

	// Returns null when the page has no cart drawer to read a verdict from, so an unrelated
	// template can never be mistaken for an empty cart and wipe a running timer.
	function cartIsEmpty() {
		var drawers = document.querySelectorAll("cart-drawer");
		if (!drawers.length) return null;

		for (var i = 0; i < drawers.length; i++) {
			if (!drawers[i].classList.contains("is-empty")) return false;
		}
		return true;
	}

	function render() {
		renderScheduled = false;

		var bars = document.querySelectorAll("[data-cart-timer]");

		if (cartIsEmpty() === true) {
			if (!clearedForEmptyCart) {
				clearPersistedState();
				clearedForEmptyCart = true;
			}
			for (var e = 0; e < bars.length; e++) paint(bars[e], "empty");
			stopTicking();
			return;
		}

		clearedForEmptyCart = false;

		if (!bars.length) {
			stopTicking();
			return;
		}

		var now = Date.now();
		var running = false;

		for (var b = 0; b < bars.length; b++) {
			var bar = bars[b];
			var id = bar.getAttribute("data-cart-timer");
			if (!id) continue;

			var state = readState(id, durationOf(bar), now);

			if (state.expired) {
				paint(bar, "expired");
				continue;
			}

			var remaining = state.end - now;
			if (remaining <= 0) {
				state.expired = true;
				writeState(id, state);
				paint(bar, "expired");
				continue;
			}

			running = true;
			paint(bar, "running", remaining);
		}

		if (running) startTicking();
		else stopTicking();
	}

	// Microtask rather than rAF: a drawer swap must be reconciled in the same checkpoint as the
	// mutation that caused it, otherwise the bar paints one frame with placeholder markup.
	function scheduleRender() {
		if (renderScheduled) return;
		renderScheduled = true;
		if (typeof queueMicrotask === "function") queueMicrotask(render);
		else Promise.resolve().then(render);
	}

	function startTicking() {
		if (tickHandle !== null) return;
		tickHandle = setInterval(render, TICK_MS);
	}

	function stopTicking() {
		if (tickHandle === null) return;
		clearInterval(tickHandle);
		tickHandle = null;
	}

	function watchDrawers() {
		var drawers = document.querySelectorAll("cart-drawer");
		for (var i = 0; i < drawers.length; i++) {
			if (watchedDrawers.has(drawers[i])) continue;
			watchedDrawers.add(drawers[i]);
			drawerObserver.observe(drawers[i], { attributes: true, attributeFilter: ["class"] });
		}
	}

	function observe() {
		if (domObserver) return;

		drawerObserver = new MutationObserver(scheduleRender);
		domObserver = new MutationObserver(function (records) {
			for (var i = 0; i < records.length; i++) {
				if (records[i].addedNodes.length || records[i].removedNodes.length) {
					watchDrawers();
					scheduleRender();
					return;
				}
			}
		});

		domObserver.observe(document.body, { childList: true, subtree: true });
		watchDrawers();
	}

	function init() {
		observe();
		render();

		["cart-drawer:update", "cart-drawer:render", "cart-drawer:open", "cart:updated"].forEach(function (name) {
			document.addEventListener(name, scheduleRender);
		});

		document.addEventListener("shopify:section:load", scheduleRender);
		document.addEventListener("shopify:block:select", scheduleRender);

		// Background tabs throttle timers, so catch up the moment the page is looked at again.
		document.addEventListener("visibilitychange", function () {
			if (!document.hidden) render();
		});
		window.addEventListener("pageshow", scheduleRender);
		window.addEventListener("storage", function (event) {
			if (!event.key || isTimerKey(event.key)) scheduleRender();
		});
	}

	window.Elixir_CartTimer = { render: render, refresh: scheduleRender };

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
